import { useAppSelector } from 'hooks/redux';
import React, { useMemo } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { makeStyles } from 'tss-react/mui';

import { useField } from 'app-components/flux/categories/categoryFormHooks';
import { UsageTypeEnum } from 'app-components/flux/categories/categoriesTypes';
import { taxFormsSelectors } from 'app-components/flux/tax-forms';
import { taxLineItemsSelectors } from 'app-components/flux/tax-line-items';

import InfoTooltipIcon from 'components/InfoTooltipIcon';
import TaxNavButton from './TaxNavButton';

const PERSONAL_TAX_INFO_TOOLTIP =
  'Associate this category with corresponding tax lines. This is reflected in the Reports > Taxes section.';

export type PersonalTaxSummaryView =
  | { kind: 'none' }
  | { kind: 'empty' }
  | { kind: 'not_related' }
  | { kind: 'filled'; formName: string; lineName: string; lineDescription: string };

const useStyles = makeStyles()((theme) => ({
  taxInfoCard: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: theme.spacing(1.5),
    boxShadow: theme.shadows[2],
    padding: theme.spacing(2),
  },
  notRelatedRow: {
    minHeight: 24,
    width: '100%',
  },
  taxInfoHeading: {
    flex: '1 1 auto',
    lineHeight: '20px',
    color: theme.palette.text.primary,
    minWidth: 0,
  },
  bodyStack: {
    paddingTop: theme.spacing(2),
  },
  formColumn: {
    width: 99,
    flexShrink: 0,
  },
  taxLineColumn: {
    flex: '1 1 0',
    minWidth: 0,
  },
  captionText: {
    lineHeight: '16px',
  },
  bodyText: {
    lineHeight: '20px',
    wordBreak: 'break-word',
  },
  taxLineRow: {
    minWidth: 0,
  },
  tooltipBox: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    color: theme.palette.text.secondary,
    '& .MuiSvgIcon-root': { fontSize: 16 },
  },
}));

export const usePersonalTaxSummary = (): PersonalTaxSummaryView => {
  const { value: usageType } = useField('usageType');
  const { value: taxRelated } = useField('taxRelated');
  const { value: taxForm } = useField('taxForm');
  const { value: txfId } = useField('txfId');

  const taxFormResource = useAppSelector((s) => (taxForm ? taxFormsSelectors.getTaxFormForId(s, taxForm) : undefined));
  const taxLineResource = useAppSelector((s) =>
    txfId ? taxLineItemsSelectors.getTaxLineItemForId(s, txfId) : undefined,
  );

  return useMemo(() => {
    if (usageType !== UsageTypeEnum.PERSONAL) {
      return { kind: 'none' } as const;
    }
    if (!taxRelated) {
      return { kind: 'not_related' } as const;
    }
    if (!taxForm || !txfId) {
      return { kind: 'empty' } as const;
    }

    return {
      kind: 'filled' as const,
      formName: taxFormResource?.get?.('name') ?? '',
      lineName: taxLineResource?.get?.('name') ?? '',
      lineDescription: taxLineResource?.get?.('description') ?? '',
    };
  }, [taxForm, taxFormResource, taxLineResource, taxRelated, txfId, usageType]);
};

type Props = {
  view: PersonalTaxSummaryView;
  onEdit: () => void;
};

const PersonalTaxInfoSummary = ({ view, onEdit }: Props) => {
  const { classes } = useStyles();

  if (view.kind === 'none' || view.kind === 'empty') {
    return null;
  }

  if (view.kind === 'not_related') {
    // Figma: Tax info preview — "during create" / not yet tax-related (node 1851:3787, ref. 1851:2904)
    return (
      <Stack
        data-testid='personal-tax-info-not-related'
        direction='row'
        alignItems='center'
        justifyContent='flex-end'
        columnGap={1.5}
        className={classes.notRelatedRow}
      >
        <TaxNavButton
          id='add-personal-tax-info'
          text='Tax Info'
          infoText={PERSONAL_TAX_INFO_TOOLTIP}
          onClick={onEdit}
          ariaLabel='Add tax info'
        />
      </Stack>
    );
  }

  return (
    <Card elevation={0} data-testid='personal-tax-info-summary' className={classes.taxInfoCard}>
      <Stack direction='row' alignItems='flex-start' justifyContent='space-between' columnGap={1.5}>
        <Typography component='span' variant='bodyMedium' className={classes.taxInfoHeading}>
          Tax info
        </Typography>
        <TaxNavButton id='personal-tax-info-edit' text='Edit' onClick={onEdit} ariaLabel='Edit tax info' />
      </Stack>

      <Stack direction='row' alignItems='flex-start' columnGap={5} className={classes.bodyStack}>
        <Stack spacing={0.5} className={classes.formColumn}>
          <Typography variant='captionRegular' color='text.secondary' className={classes.captionText}>
            Form
          </Typography>
          <Typography variant='bodyRegular' className={classes.bodyText}>
            {view.formName || '—'}
          </Typography>
        </Stack>
        <Stack spacing={0.5} className={classes.taxLineColumn}>
          <Typography variant='captionRegular' color='text.secondary' className={classes.captionText}>
            Tax line
          </Typography>
          <Stack direction='row' alignItems='center' columnGap={0.5} className={classes.taxLineRow}>
            {view.lineDescription ? (
              <div className={classes.tooltipBox}>
                <InfoTooltipIcon size='small-no-padding' tooltipText={view.lineDescription} />
              </div>
            ) : null}
            <Typography variant='bodyRegular' className={classes.bodyText}>
              {view.lineName || '—'}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

export default PersonalTaxInfoSummary;
